/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import {
  collection, query, where, getDocs, getDoc,
  doc, setDoc, serverTimestamp, onSnapshot, limit
} from 'firebase/firestore';
import { Avatar } from './ui';

// ─────────────────────────────────────────────────────────────────────────────
// BuddyReads — leer un libro con un amigo
//
// Props:
//   userId    — UID del usuario actual
//   perfil    — { nombre, foto } del usuario actual
//   libroId   — ID del libro
//   libro     — { titulo, autor, portada }
//   esPremium — boolean (gratis: 1 buddy read activo, premium: ilimitados)
//   onUpgrade — fn para abrir modal premium
//   onEntrarSala — fn para entrar en la sala (entrarEnSala de App.js)
//   listaSiguiendo — array de usuarios que sigo [{ id, nombre, foto }]
//
// Colección Firestore:
//   buddy_reads/{buddyId}
//     libroId, titulo, autor, portada
//     usuarioA: { id, nombre, foto }, usuarioB: { id, nombre, foto }
//     progreso: { [userId]: { pagina, total, pct, fecha } }
//     activo: true
//     creadoEn: timestamp
//     salaId: "buddy_{uidA}_{uidB}_{libroId}"
// ─────────────────────────────────────────────────────────────────────────────

const MAX_BUDDY_GRATIS = 1;

function generarBuddyId(uidA, uidB, libroId) {
  // ID determinista — mismo resultado independientemente del orden
  const [u1, u2] = [uidA, uidB].sort();
  return `buddy_${u1}_${u2}_${libroId}`;
}

// ── Barra de progreso ─────────────────────────────────────────────────────────
function BarraProgreso({ pct, color, nombre }) {
  const [ancho, setAncho] = useState(0);
  useEffect(() => { const t = setTimeout(() => setAncho(pct), 100); return () => clearTimeout(t); }, [pct]);
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '12px', color: '#aaa' }}>{nombre}</span>
        <span style={{ fontSize: '12px', color, fontWeight: 'bold' }}>{Math.round(pct)}%</span>
      </div>
      <div style={{ height: '5px', background: '#1a1a1a', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${ancho}%`, background: color, borderRadius: '99px', transition: '0.8s ease' }} />
      </div>
    </div>
  );
}

// ── Modal selector de amigo ───────────────────────────────────────────────────
function ModalElegirAmigo({ userId, listaSiguiendo, libro, onConfirmar, onCerrar }) {
  const [seleccionado, setSeleccionado] = useState(null);
  const [buscando, setBuscando] = useState('');
  const [creando, setCreando] = useState(false);

  const filtrados = listaSiguiendo.filter(u =>
    u.nombre?.toLowerCase().includes(buscando.toLowerCase()) && u.id !== userId
  );

  return (
    <div className="modal-overlay" onClick={onCerrar}>
      <div className="modal-content" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: '0 0 2px', fontSize: '18px', fontWeight: 900 }}>Buddy Read</h3>
            <p style={{ margin: 0, fontSize: '12px', color: '#555' }}>{libro.titulo}</p>
          </div>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '20px' }}>✕</button>
        </div>

        <p style={{ fontSize: '13px', color: '#555', margin: '0 0 14px' }}>
          Elige a alguien que sigues para leer este libro juntos:
        </p>

        {listaSiguiendo.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#333', fontSize: '13px' }}>
            Sigue a otros lectores para poder hacer buddy reads con ellos.
          </div>
        ) : (
          <>
            <input
              placeholder="Buscar por nombre..."
              value={buscando}
              onChange={e => setBuscando(e.target.value)}
              className="input-modal"
              style={{ marginBottom: '12px' }}
            />
            <div style={{ maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
              {filtrados.length === 0 ? (
                <p style={{ color: '#333', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>No hay resultados</p>
              ) : filtrados.map(u => (
                <button
                  key={u.id}
                  onClick={() => setSeleccionado(seleccionado?.id === u.id ? null : u)}
                  style={{
                    background: seleccionado?.id === u.id ? '#1DB95418' : '#0a0a0a',
                    border: `1px solid ${seleccionado?.id === u.id ? '#1DB95466' : '#1e1e1e'}`,
                    borderRadius: '10px',
                    padding: '10px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: '0.15s',
                    textAlign: 'left',
                  }}
                >
                  <Avatar src={u.foto} nombre={u.nombre} size={36} />
                  <span style={{ fontSize: '14px', color: 'white', fontWeight: seleccionado?.id === u.id ? 'bold' : 'normal' }}>
                    {u.nombre}
                  </span>
                  {seleccionado?.id === u.id && (
                    <span style={{ marginLeft: 'auto', color: '#1DB954', fontSize: '16px' }}>✓</span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}

        <button
          onClick={async () => {
            if (!seleccionado || creando) return;
            setCreando(true);
            await onConfirmar(seleccionado);
            setCreando(false);
          }}
          disabled={!seleccionado || creando}
          className="btn-verde"
          style={{ marginTop: 0 }}
        >
          {creando ? 'Creando...' : '📖 Empezar Buddy Read'}
        </button>
      </div>
    </div>
  );
}

// ── Tarjeta de buddy read activo ──────────────────────────────────────────────
function TarjetaBuddy({ buddy, userId, libro, onEntrarSala, onActualizarProgreso }) {
  const [editandoPagina, setEditandoPagina] = useState(false);
  const [pagina, setPagina] = useState('');
  const [total, setTotal] = useState('');
  const [guardando, setGuardando] = useState(false);

  const esA = buddy.usuarioA?.id === userId;
  const yo = esA ? buddy.usuarioA : buddy.usuarioB;
  const otro = esA ? buddy.usuarioB : buddy.usuarioA;
  const miProgreso = buddy.progreso?.[userId] || {};
  const suProgreso = buddy.progreso?.[otro?.id] || {};

  const miPct = miProgreso.pct || 0;
  const suPct = suProgreso.pct || 0;

  const guardarProgreso = async () => {
    const pag = parseInt(pagina);
    const tot = parseInt(total);
    if (!pag || !tot || pag > tot) return;
    setGuardando(true);
    try {
      await setDoc(doc(db, 'buddy_reads', buddy.id), {
        progreso: {
          ...buddy.progreso,
          [userId]: {
            pagina: pag,
            total: tot,
            pct: Math.round((pag / tot) * 100),
            fecha: serverTimestamp(),
          }
        }
      }, { merge: true });
      setEditandoPagina(false);
      if (onActualizarProgreso) onActualizarProgreso();
    } catch { }
    setGuardando(false);
  };

  return (
    <div style={{ background: '#0a0a0a', border: '1px solid #1e1e1e', borderRadius: '12px', padding: '14px', marginBottom: '8px' }}>
      {/* Cabecera con ambos usuarios */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <Avatar src={yo?.foto} nombre={yo?.nombre} size={28} />
        <span style={{ fontSize: '11px', color: '#555' }}>+</span>
        <Avatar src={otro?.foto} nombre={otro?.nombre} size={28} />
        <span style={{ fontSize: '13px', color: '#aaa', fontWeight: 'bold', marginLeft: '4px' }}>
          {otro?.nombre || 'Tu buddy'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#333', background: '#141414', padding: '3px 8px', borderRadius: '8px', border: '1px solid #1e1e1e' }}>
          BUDDY READ
        </span>
      </div>

      {/* Progreso */}
      <div style={{ marginBottom: '10px' }}>
        <BarraProgreso pct={miPct} color="#1DB954" nombre={`Tú${miProgreso.pagina ? ` — pág. ${miProgreso.pagina}` : ''}`} />
        <BarraProgreso pct={suPct} color="#a78bfa" nombre={`${otro?.nombre?.split(' ')[0] || 'Buddy'}${suProgreso.pagina ? ` — pág. ${suProgreso.pagina}` : ''}`} />
      </div>

      {/* Editar mi progreso */}
      {editandoPagina ? (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
          <input
            type="number"
            placeholder="Pág."
            value={pagina}
            onChange={e => setPagina(e.target.value)}
            style={{ width: '70px', background: '#141414', border: '1px solid #2a2a2a', color: 'white', padding: '6px 8px', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
          />
          <span style={{ color: '#333', fontSize: '12px' }}>/</span>
          <input
            type="number"
            placeholder="Total"
            value={total}
            onChange={e => setTotal(e.target.value)}
            style={{ width: '80px', background: '#141414', border: '1px solid #2a2a2a', color: 'white', padding: '6px 8px', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
          />
          <button onClick={guardarProgreso} disabled={guardando}
            style={{ background: '#1DB954', color: 'black', border: 'none', borderRadius: '8px', padding: '6px 12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', flex: 1 }}>
            {guardando ? '...' : 'Guardar'}
          </button>
          <button onClick={() => setEditandoPagina(false)}
            style={{ background: 'none', border: '1px solid #2a2a2a', color: '#555', borderRadius: '8px', padding: '6px 8px', cursor: 'pointer', fontSize: '12px' }}>
            ✕
          </button>
        </div>
      ) : (
        <button onClick={() => {
          setPagina(miProgreso.pagina || '');
          setTotal(miProgreso.total || '');
          setEditandoPagina(true);
        }}
          style={{ background: 'none', border: '1px solid #1e1e1e', color: '#444', borderRadius: '8px', padding: '5px 10px', cursor: 'pointer', fontSize: '11px', marginBottom: '8px', transition: '0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.color = '#888'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e1e1e'; e.currentTarget.style.color = '#444'; }}
        >
          Actualizar mi progreso
        </button>
      )}

      {/* Ir al chat */}
      <button
        onClick={() => onEntrarSala(libro, false, buddy.salaId, null, `📖 ${libro.titulo} — Buddy Read`)}
        style={{ width: '100%', background: '#1DB95418', color: '#1DB954', border: '1px solid #1DB95433', borderRadius: '10px', padding: '9px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: '0.15s' }}
        onMouseEnter={e => e.currentTarget.style.background = '#1DB95428'}
        onMouseLeave={e => e.currentTarget.style.background = '#1DB95418'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/></svg>
        Chat del buddy read
      </button>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export function BuddyReads({ userId, perfil, libroId, libro, esPremium, onUpgrade, onEntrarSala, listaSiguiendo }) {
  const [buddies, setBuddies] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [recargar, setRecargar] = useState(0);

  // Cargar buddy reads de este libro
  useEffect(() => {
    if (!userId || !libroId) { setCargando(false); return; }
    let activo = true;
    const cargar = async () => {
      setCargando(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, 'buddy_reads'),
            where('libroId', '==', libroId),
            where('activo', '==', true),
            limit(20)
          )
        );
        const mios = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(b => b.usuarioA?.id === userId || b.usuarioB?.id === userId);
        if (activo) setBuddies(mios);
      } catch { }
      if (activo) setCargando(false);
    };
    cargar();
    return () => { activo = false; };
  }, [userId, libroId, recargar]);

  const buddiesActivos = buddies.filter(b => b.activo);
  const puedeCrear = esPremium || buddiesActivos.length < MAX_BUDDY_GRATIS;

  const crearBuddy = async (amigo) => {
    try {
      const buddyId = generarBuddyId(userId, amigo.id, libroId);

      // Verificar si ya existe
      const existeSnap = await getDoc(doc(db, 'buddy_reads', buddyId));
      if (existeSnap.exists() && existeSnap.data().activo) {
        setModalVisible(false);
        return;
      }

      const salaId = buddyId; // La sala tiene el mismo ID que el buddy read

      await setDoc(doc(db, 'buddy_reads', buddyId), {
        libroId,
        titulo: libro.titulo || '',
        autor: libro.autor || '',
        portada: libro.portada || '',
        usuarioA: { id: userId, nombre: perfil.nombre || '', foto: perfil.foto || null },
        usuarioB: { id: amigo.id, nombre: amigo.nombre || '', foto: amigo.foto || null },
        progreso: {},
        activo: true,
        salaId,
        creadoEn: serverTimestamp(),
        creadoPor: userId,
      });

      setModalVisible(false);
      setRecargar(r => r + 1);

      // Entrar directamente al chat del buddy read
      onEntrarSala(libro, false, salaId, null, `📖 ${libro.titulo} — Buddy Read`);
    } catch (e) { console.error(e); }
  };

  return (
    <>
      {modalVisible && (
        <ModalElegirAmigo
          userId={userId}
          listaSiguiendo={listaSiguiendo}
          libro={libro}
          onConfirmar={crearBuddy}
          onCerrar={() => setModalVisible(false)}
        />
      )}

      <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: '14px', padding: '14px 16px', margin: '14px 0' }}>
        {/* Cabecera */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: buddies.length > 0 ? '12px' : '0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#38bdf8">
              <path d="M12 12.75c1.63 0 3.07.39 4.24.9 1.08.48 1.76 1.56 1.76 2.73V18H6v-1.61c0-1.18.68-2.26 1.76-2.73 1.17-.52 2.61-.91 4.24-.91zM4 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm1.13 1.1c-.37-.06-.74-.1-1.13-.1-.99 0-1.93.21-2.78.58C.48 14.9 0 15.62 0 16.43V18h4.5v-1.61c0-.83.23-1.61.63-2.29zM20 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm4 3.43c0-.81-.48-1.53-1.22-1.85A6.95 6.95 0 0020 14c-.39 0-.76.04-1.13.1.4.68.63 1.46.63 2.29V18H24v-1.57zM12 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z"/>
            </svg>
            <span style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>
              Buddy Reads
            </span>
            {buddies.length > 0 && (
              <span style={{ background: '#38bdf822', color: '#38bdf8', fontSize: '10px', fontWeight: 'bold', padding: '1px 7px', borderRadius: '10px' }}>
                {buddies.length}
              </span>
            )}
          </div>

          <button
            onClick={() => {
              if (!puedeCrear) { onUpgrade(); return; }
              setModalVisible(true);
            }}
            style={{
              background: puedeCrear ? '#38bdf822' : 'transparent',
              color: puedeCrear ? '#38bdf8' : '#333',
              border: `1px solid ${puedeCrear ? '#38bdf844' : '#1e1e1e'}`,
              borderRadius: '20px',
              padding: '5px 12px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              transition: '0.15s',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            {!puedeCrear && <span style={{ fontSize: '10px' }}>✦</span>}
            + Invitar
          </button>
        </div>

        {/* Lista de buddy reads */}
        {cargando ? (
          <div style={{ height: '40px', background: '#1a1a1a', borderRadius: '8px', animation: 'pulse 1.5s infinite' }} />
        ) : buddies.length === 0 ? (
          <p style={{ fontSize: '12px', color: '#333', margin: '10px 0 0', fontStyle: 'italic' }}>
            Lee este libro con un amigo y compara vuestro progreso.
          </p>
        ) : (
          buddies.map(b => (
            <TarjetaBuddy
              key={b.id}
              buddy={b}
              userId={userId}
              libro={libro}
              onEntrarSala={onEntrarSala}
              onActualizarProgreso={() => setRecargar(r => r + 1)}
            />
          ))
        )}

        {/* Aviso límite gratis */}
        {!esPremium && buddiesActivos.length >= MAX_BUDDY_GRATIS && (
          <div style={{ background: 'linear-gradient(135deg, #1a0533, #1a1a2e)', border: '1px solid #a78bfa33', borderRadius: '10px', padding: '10px 14px', marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#7c5fad' }}>
              Gratis: 1 buddy read activo. Premium: ilimitados.
            </p>
            <button onClick={onUpgrade}
              style={{ background: 'linear-gradient(135deg, #6a3fa0, #a78bfa)', color: 'white', border: 'none', borderRadius: '15px', padding: '5px 12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px', flexShrink: 0 }}>
              ✦ Premium
            </button>
          </div>
        )}
      </div>
    </>
  );
}
